import { Component, OnInit, signal, computed } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { BaseComponent } from '../../../../core/abstracts/baseComponent';
import { Actions, ofType} from '@ngrx/effects';
import { State } from '../../../../core/store';
import { Store } from '@ngrx/store';
import { BenefitPlanWithdrawnReportModel } from '../../../../models/benefitsModels';
import { DateRangeModel } from '../../../../models/dateRangeModel';
import { PagingResultsModel } from '../../../../models/pagingResultsModel';
import { DropdownFilterService } from '../../../../core/services/dropdown-filter.service';
import { deepClone, downloadFile } from '../../../../helpers/utils';
import { loadEmployeeUsers, loadViewableCampuses } from '../../../../core/store/employee/employee.action';
import { exportBenefitWithdrawnToExcel, exportBenefitWithdrawnToExcelSuccess, getBenefitWithdrawnList, loadBenfitPlanTypes, loadWithdrawnReasons } from '../../../../core/store/benefits/benefits.actions';
import { tap } from 'rxjs';
import { UserAutoCompleteItem, ModifiedSelectItem } from '../../../../models/utilModels';

@Component({
    selector: 'app-list-benefit-withdrawn',
    templateUrl: './list-benefit-withdrawn.component.html',
    styleUrls: ['./list-benefit-withdrawn.component.scss'],
    standalone: false
})
export class ListBenefitWithdrawnComponent extends BaseComponent implements OnInit {
  list: PagingResultsModel<BenefitPlanWithdrawnReportModel>;
  filter: any;
  canEdit: boolean = false;

  loadingList: boolean = true;
  loadingExcel: boolean = false;

  campusList: ModifiedSelectItem[];
  campusId: number = 0;

  selectedUserEmployee: UserAutoCompleteItem;
  userEmployeeList: UserAutoCompleteItem[];
  userId: number = 0;

  dateRangeList: DateRangeModel[];
  dateRange: DateRangeModel;
  startDate: string = null;
  endDate: string = null;
  isCustomDateRange: boolean = false;

  planTypes: ModifiedSelectItem[];
  planTypeId: number = 0;

  withdrawnReasons: ModifiedSelectItem[];
  withdrawnReasonId: number = 0;

  carrier: string = "";

  totalRecords = signal<number>(0);
  dropdownOptions = computed(() => {
    var total = this.totalRecords();
    return [
      { label: '20', value: 20 },
      { label: '50', value: 50 },
      { label: '100', value: 100 },
      { label: '200', value: 200 },
      { label: '500', value: 500 },
      { label: 'All', value: total > 0 ? total : 20 } 
    ];
  });

  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private dropdownFilterService: DropdownFilterService) {
    super();
  }

  ngOnInit() {
    this.subs.push(
      this.store.select(s => s.benefitsState.loadingWithdrawnList).subscribe(loadingWithdrawnList => {
        this.loadingList = loadingWithdrawnList;
      }),
      this.store.select(s => s.benefitsState.loadingWithdrawnExcel).subscribe(loadingWithdrawnExcel => {
        this.loadingExcel = loadingWithdrawnExcel;
      }),
      this.store.select(s => s.accountState.user?.permissionIDs).subscribe(permissionIDs => {
        this.canEdit = permissionIDs && !!permissionIDs.find(p => p === 3075);
      }),
      this.store.select(s => s.employeeState.campuses).subscribe(campuses => {
        this.campusList = campuses && campuses.length > 0 ? campuses
          : [{ label: 'loading...', value: undefined }];
      }),
      this.store.select(s => s.benefitsState.benefitPlanTypesList).subscribe(planTypes => {
        if (planTypes) {
          this.planTypes = planTypes.map(x => ({ label: x.planTypeName, value: x.id }));
          this.planTypes.unshift({ label: 'All', value: 0 });
        }
        else {
          this.planTypes = [{ label: 'loading...', value: undefined }]
        }
      }),
      this.store.select(s => s.benefitsState.withdrawnReasons).subscribe(reasons => {
        if (reasons) {
          this.withdrawnReasons = reasons.map(x => ({ label: x.label, value: x.value }));
          this.withdrawnReasons.unshift({ label: 'All', value: 0 });
        }
        else {
          this.withdrawnReasons = [{ label: 'loading...', value: undefined }]
        }
      }),
      this.store.select(s => s.employeeState.employeeUsers).subscribe(users => {
        this.userEmployeeList = users.map(x => ({
          id: x.value,
          name: x.label
        }));
      }),
      this.store.select(s => s.benefitsState.withdrawnList).subscribe(list => {
        this.list = list;  
        if(list != null)
          this.totalRecords.set(list.totalRecords);
      }),
      this.actions$.pipe(
        ofType(exportBenefitWithdrawnToExcelSuccess),
        tap(action => {
          downloadFile(action.doc, 'BenefitWithdrawnReport.xlsx');
        })
      ).subscribe()
    );

    this.dateRangeList = this.dropdownFilterService.getLast120DayDateRanges();
    if (this.dateRangeList && this.dateRangeList.length > 0) {
      this.dateRange = this.dateRangeList[0];
    }
    this.store.dispatch(loadViewableCampuses());
    this.store.dispatch(loadBenfitPlanTypes());
    this.store.dispatch(loadWithdrawnReasons());
  }

  changedDateRange() {
    if (this.dateRange.isCustom) {
      this.isCustomDateRange = true;
      return; //reload after manually changing date
    }

    this.isCustomDateRange = false;
    this.startDate = this.dateRange.startDate;
    this.endDate = this.dateRange.endDate;
    this.reloadList();
  }

  reloadFilter(event) {
    this.filter = event;
    this.reloadList();
  }

  reloadList() {
    this.filter = deepClone(this.filter);
    this.filter.filters.userId = { Name: 'userId', Value: { value: this.userId.toString() } };
    this.filter.filters.programId = { Name: 'programId', Value: { value: this.campusId.toString() } };
    this.filter.filters.startDate = { Name: 'startDate', Value: { value: this.startDate } };
    this.filter.filters.endDate = { Name: 'endDate', Value: { value: this.endDate } };
    this.filter.filters.planTypeId = { Name: 'planTypeId', Value: { value: this.planTypeId } };
    this.filter.filters.withdrawnReasonId = { Name: 'withdrawnReasonId', Value: { value: this.withdrawnReasonId } };
    this.filter.filters.carrier = { Name: 'carrier', Value: { value: this.carrier } };

    this.store.dispatch(getBenefitWithdrawnList({
      filter: this.filter
    }));
  }

  getStaffList(event) {
    this.store.dispatch(loadEmployeeUsers({
      prefixText: event.query,
      programId: this.campusId,
      payrollClassificationId: null,
      showInactive: false,
      showTerminated: false
    }));
  }

  reloadListAfterStaffChage(selectedUser) {
    if (typeof selectedUser.value.id !== 'number') {
      return;
    }

    this.userId = selectedUser.value.id;
    this.reloadList();
  }

  reloadListAfterClear(event) {
    
    this.userId = 0;
    this.reloadList();
  }

  exportToExcel() {
    this.filter = deepClone(this.filter);
    this.filter.filters.userId = { Name: 'userId', Value: { value: this.userId.toString() } };
    this.filter.filters.programId = { Name: 'programId', Value: { value: this.campusId.toString() } };
    this.filter.filters.startDate = { Name: 'startDate', Value: { value: this.startDate } };
    this.filter.filters.endDate = { Name: 'endDate', Value: { value: this.endDate } };
    this.filter.filters.planTypeId = { Name: 'planTypeId', Value: { value: this.planTypeId } };
    this.filter.filters.withdrawnReasonId = { Name: 'withdrawnReasonId', Value: { value: this.withdrawnReasonId } };
    this.filter.filters.carrier = { Name: 'carrier', Value: { value: this.carrier } };

    this.store.dispatch(exportBenefitWithdrawnToExcel({
      filter: this.filter
    }));
  }
}
